import { Box } from '@mui/material';
import { Button } from 'system/atoms';
import { Dropdown } from 'components/DropDown';
import { ModalContainer } from 'components/Modals/ModalContainer';
import { useCandidateDecision } from 'pages/CandidatesRanking/hooks/useCandidateDecision';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useMutation } from 'services/api/hooks';
import { updateCandidate } from 'services/candidate/api';
import { candidateRejectionModalAtom } from 'services/candidate/atoms';
import { CandidateStatus } from 'services/candidate/types';
import { integrationAtom } from 'services/integration/atom';
import {
  useInterviewStages,
  useRejectedReasons
} from 'services/integration/hooks';
import { isInterviewStageBasedRejection } from 'services/integration/utils';
import { getRejectionDropdownOptions } from 'services/integration/utils/getRejectionDropdownOptions';

const buttonsContainer = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4
};

type Props = {
  atsJobId: string | undefined | null;
};

export const CandidateRejectionModal: React.FC<Props> = ({ atsJobId }) => {
  const integration = useRecoilValue(integrationAtom);
  const useStagesForRejection = isInterviewStageBasedRejection(integration);

  const { isEnabled: isRejectionReasonsEnabled, rejectedReasons } =
    useRejectedReasons({ atsJobId });
  const { interviewStages } = useInterviewStages({ atsJobId });

  const [selectedId, setSelectedId] = useState<string>('');
  const { candidateIds, candidateBatchId, isOpen } = useRecoilValue(
    candidateRejectionModalAtom
  );
  const { closeModals } = useCandidateDecision({ atsJobId });

  const { mutate: updateCandidateAction } = useMutation(updateCandidate, {
    onSuccess: () => {
      closeModals();
    },
    disableSuccessToast: true
  });

  const dropdownOptions = getRejectionDropdownOptions({
    useStagesForRejection,
    interviewStages,
    rejectedReasons
  });

  const isDropdownVisible = useStagesForRejection
    ? interviewStages.length > 0
    : isRejectionReasonsEnabled;

  const subtitle = useMemo(() => {
    if (useStagesForRejection && interviewStages.length > 0) {
      return 'Select the ATS stage to move the candidate into';
    }
    if (isRejectionReasonsEnabled) {
      return 'Select a reason to why you are rejecting the candidate';
    }
    return 'Rejecting candidate will change their status';
  }, [
    isRejectionReasonsEnabled,
    useStagesForRejection,
    interviewStages.length
  ]);

  const reject = async (selectedOptionId: string | undefined) => {
    if (!candidateIds.length || !candidateBatchId) {
      return;
    }

    if (useStagesForRejection) {
      const selectedStage = interviewStages.find(
        (s) => s.id === selectedOptionId
      );

      await Promise.all(
        candidateIds.map((candidateId) =>
          updateCandidateAction({
            id: candidateId,
            status: CandidateStatus.REJECTED,
            atsInterviewStageId:
              selectedStage?.remoteId || selectedOptionId || undefined,
            komboStageId: selectedStage?.remoteId ? selectedOptionId : undefined
          })
        )
      );
      return;
    }

    const selectedReason = rejectedReasons.find(
      (r) => r.id === selectedOptionId
    );

    await Promise.all(
      candidateIds.map((candidateId) =>
        updateCandidateAction({
          id: candidateId,
          status: CandidateStatus.REJECTED,
          atsRejectedReasonId:
            selectedReason?.remoteId || selectedOptionId || undefined,
          komboRejectionReasonId: selectedReason?.remoteId
            ? selectedOptionId
            : undefined
        })
      )
    );
  };

  const onRejectClick = async () => {
    closeModals();
    await reject(selectedId);
  };

  return (
    <ModalContainer
      title="Are you sure?"
      open={isOpen}
      onClose={closeModals}
      subtitle={subtitle}
      containerSx={{
        maxWidth: 400
      }}
    >
      {isDropdownVisible && (
        <Dropdown
          options={dropdownOptions}
          onChange={(id) => setSelectedId(id)}
          value={selectedId}
          placeholder={
            useStagesForRejection
              ? 'ATS stage to apply'
              : 'Reason for rejection'
          }
        />
      )}
      <Box sx={buttonsContainer}>
        <Button onClick={onRejectClick} variant="primary" fullWidth>
          Reject
        </Button>
        <Button
          onClick={closeModals}
          variant="tertiary"
          state="destructive"
          fullWidth
        >
          Cancel
        </Button>
      </Box>
    </ModalContainer>
  );
};
